import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../../components/Modal';
import { useContacts, useDeleteContact } from '../hooks';
import { ContactFormModal } from './ContactFormModal';
import { BusinessCardScanner } from './BusinessCardScanner';
import type { ContactPrefillData } from './ContactFormModal';
import type { Contact, ContactType, ContactsQueryParams } from '../types';
import styles from './ContactsPage.module.css';

const TYPE_COLORS: Record<ContactType, { bg: string; color: string }> = {
  PERSON: { bg: '#dbeafe', color: '#1e40af' },
  ORGANIZATION: { bg: '#d1fae5', color: '#065f46' },
};

const TYPE_LABELS: Record<ContactType, string> = {
  PERSON: 'Person',
  ORGANIZATION: 'Organisation',
};

const thStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  textAlign: 'left',
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  color: 'var(--color-text-muted)',
};

function contactDisplayName(c: Contact): string {
  if (c.type === 'ORGANIZATION') return c.companyName ?? '—';
  return [c.firstName, c.lastName].filter(Boolean).join(' ') || '—';
}

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

export function ContactsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [letterFilter, setLetterFilter] = useState('');
  const [isCreateOpen, setCreateOpen] = useState(false);
  const [isScannerOpen, setScannerOpen] = useState(false);
  const [scanPrefill, setScanPrefill] = useState<ContactPrefillData | null>(null);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [deletingContact, setDeletingContact] = useState<Contact | null>(null);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const params: ContactsQueryParams = {
    page,
    pageSize: 25,
    ...(search ? { search } : {}),
    ...(letterFilter ? { letter: letterFilter } : {}),
    sort: 'lastName',
    order: 'asc',
  };

  const { data, isLoading, error } = useContacts(params);
  const deleteMutation = useDeleteContact();

  if (isLoading) return <p>Laden...</p>;
  if (error)
    return (
      <p style={{ color: 'var(--color-error)' }}>
        Fehler beim Laden der Kontakte
      </p>
    );

  const contacts = data?.data ?? [];
  const pagination = data?.pagination;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>Kontakte</h1>
          <span style={{ fontSize: '0.8125rem', background: '#eff6ff', color: '#2563eb', borderRadius: '9999px', padding: '0.1875rem 0.625rem', fontWeight: 600 }}>
            {pagination?.total ?? 0}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button
            onClick={() => setScannerOpen(true)}
            style={{
              padding: '0.5rem 0.875rem',
              background: 'transparent',
              color: 'var(--color-text-secondary)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
            Visitenkarte scannen
          </button>
          <button
            onClick={() => setCreateOpen(true)}
            style={{
              padding: '0.5rem 1.25rem',
              background: 'var(--color-primary)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            + Neuer Kontakt
          </button>
        </div>
      </div>

      {/* Filterleiste */}
      <div className={styles.filterBar}>
        <input
          type="search"
          placeholder="Suche nach Name, Firma, E-Mail..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className={styles.searchInput}
        />
      </div>

      {/* A-Z Filter */}
      <div style={{ display: 'flex', gap: '0.125rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <button
          onClick={() => { setLetterFilter(''); setPage(1); }}
          style={{
            padding: '0.25rem 0.5rem', fontSize: '0.75rem', fontWeight: !letterFilter ? 700 : 500,
            background: !letterFilter ? 'var(--color-primary)' : 'transparent',
            color: !letterFilter ? 'white' : 'var(--color-text-muted)',
            border: !letterFilter ? 'none' : '1px solid var(--color-border)',
            borderRadius: 'var(--radius-sm)', cursor: 'pointer', minWidth: 36,
          }}
        >
          Alle
        </button>
        {ALPHABET.map((l) => (
          <button
            key={l}
            onClick={() => { setLetterFilter(letterFilter === l ? '' : l); setPage(1); }}
            style={{
              padding: '0.25rem 0.375rem', fontSize: '0.75rem', fontWeight: letterFilter === l ? 700 : 500,
              background: letterFilter === l ? 'var(--color-primary)' : 'transparent',
              color: letterFilter === l ? 'white' : 'var(--color-text-muted)',
              border: letterFilter === l ? 'none' : '1px solid var(--color-border)',
              borderRadius: 'var(--radius-sm)', cursor: 'pointer', minWidth: 28,
            }}
          >
            {l}
          </button>
        ))}
      </div>

      {/* Tabelle */}
      <div
        style={{
          background: 'var(--color-bg-card)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-sm)',
          border: '1px solid var(--color-border)',
          overflow: 'hidden',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr
              style={{
                borderBottom: '1px solid var(--color-border)',
                background: 'var(--color-bg)',
              }}
            >
              <th style={thStyle}>Name / Firma</th>
              <th style={thStyle}>Typ</th>
              <th style={thStyle}>Unternehmen</th>
              <th style={thStyle}>E-Mail</th>
              <th style={thStyle}>Telefon</th>
              <th style={thStyle}>Stadt</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {contacts.length === 0 && (
              <tr>
                <td
                  colSpan={8}
                  style={{
                    padding: '2rem',
                    textAlign: 'center',
                    color: 'var(--color-text-muted)',
                  }}
                >
                  Keine Kontakte gefunden
                </td>
              </tr>
            )}
            {contacts.map((contact) => {
              const typeStyle = TYPE_COLORS[contact.type];
              const displayName = contactDisplayName(contact);
              const initials = contact.type === 'ORGANIZATION'
                ? (contact.companyName?.[0] ?? '?').toUpperCase()
                : ([contact.firstName?.[0], contact.lastName?.[0]].filter(Boolean).join('').toUpperCase() || '?');
              const avatarPalette = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#6366f1', '#ef4444', '#14b8a6'];
              const avatarColor = avatarPalette[(displayName.charCodeAt(0) + (displayName.charCodeAt(1) || 0)) % avatarPalette.length];
              return (
                <tr
                  key={contact.id}
                  style={{ borderBottom: '1px solid var(--color-border)', cursor: 'pointer' }}
                  onClick={() => navigate(`/crm/contacts/${contact.id}`)}
                  onMouseOver={(e) => (e.currentTarget.style.background = 'var(--color-bg)')}
                  onMouseOut={(e) => (e.currentTarget.style.background = '')}
                >
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', fontWeight: 500 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                      <span style={{
                        width: 32, height: 32, borderRadius: '50%',
                        background: avatarColor, color: 'white', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.6875rem', fontWeight: 700,
                      }}>
                        {initials}
                      </span>
                      {displayName}
                    </div>
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '0.125rem 0.5rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        background: typeStyle.bg,
                        color: typeStyle.color,
                      }}
                    >
                      {TYPE_LABELS[contact.type]}
                    </span>
                  </td>
                  <td
                    style={{
                      padding: '0.75rem 1rem',
                      fontSize: '0.875rem',
                      color: 'var(--color-text-secondary)',
                    }}
                    onClick={(e) => {
                      if (contact.company) e.stopPropagation();
                    }}
                  >
                    {contact.company ? (
                      <a
                        href={`/crm/companies/${contact.company.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(`/crm/companies/${contact.company!.id}`);
                        }}
                        style={{ color: 'var(--color-primary)' }}
                      >
                        {contact.company.name}
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td
                    style={{
                      padding: '0.75rem 1rem',
                      fontSize: '0.875rem',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    {contact.email ?? '—'}
                  </td>
                  <td
                    style={{
                      padding: '0.75rem 1rem',
                      fontSize: '0.875rem',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    {contact.phone ?? contact.mobile ?? '—'}
                  </td>
                  <td
                    style={{
                      padding: '0.75rem 1rem',
                      fontSize: '0.875rem',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    {contact.city || (contact as any).company?.city || '—'}
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: contact.isActive
                          ? 'var(--color-success)'
                          : 'var(--color-error)',
                        marginRight: '0.375rem',
                      }}
                    />
                    <span style={{ fontSize: '0.875rem' }}>
                      {contact.isActive ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }} onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                      <button
                        onClick={() => setEditingContact(contact)}
                        title="Bearbeiten"
                        style={{
                          width: 28, height: 28, padding: '0.25rem',
                          background: 'transparent', border: '1px solid var(--color-border)',
                          borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                          color: 'var(--color-text-muted)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'all 0.15s',
                        }}
                        onMouseOver={(e) => { e.currentTarget.style.borderColor = 'var(--color-primary)'; e.currentTarget.style.color = 'var(--color-primary)'; }}
                        onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.color = 'var(--color-text-muted)'; }}
                      >
                        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 1l4 4-9 9H2v-4z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => setDeletingContact(contact)}
                        title="Löschen"
                        style={{
                          width: 28, height: 28, padding: '0.25rem',
                          background: 'transparent', border: '1px solid transparent',
                          borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                          color: 'var(--color-text-muted)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'all 0.15s',
                        }}
                        onMouseOver={(e) => { e.currentTarget.style.color = 'var(--color-error)'; e.currentTarget.style.borderColor = '#fecaca'; }}
                        onMouseOut={(e) => { e.currentTarget.style.color = 'var(--color-text-muted)'; e.currentTarget.style.borderColor = 'transparent'; }}
                      >
                        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M2 4h12M5.333 4V2.667a1.333 1.333 0 011.334-1.334h2.666a1.333 1.333 0 011.334 1.334V4m2 0v9.333a1.333 1.333 0 01-1.334 1.334H4.667a1.333 1.333 0 01-1.334-1.334V4h9.334z" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Paginierung */}
        {pagination && pagination.totalPages > 1 && (
          <div className={styles.pagination}>
            <span>
              Seite {pagination.page} von {pagination.totalPages} ({pagination.total}{' '}
              Einträge)
            </span>
            <div className={styles.paginationButtons}>
              <button
                className={styles.paginationBtn}
                disabled={pagination.page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Zurück
              </button>
              <button
                className={styles.paginationBtn}
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Weiter
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal: Neuen Kontakt anlegen */}
      <ContactFormModal
        isOpen={isCreateOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={() => setCreateOpen(false)}
      />

      {/* Modal: Kontakt bearbeiten */}
      <ContactFormModal
        isOpen={!!editingContact}
        onClose={() => setEditingContact(null)}
        contact={editingContact}
        onSuccess={() => setEditingContact(null)}
      />

      {/* Modal: Kontakt löschen — Bestätigung */}
      <Modal
        isOpen={!!deletingContact}
        onClose={() => setDeletingContact(null)}
        title="Kontakt löschen"
        maxWidth="420px"
      >
        <p
          style={{
            fontSize: '0.9375rem',
            color: 'var(--color-text)',
            marginBottom: '0.5rem',
          }}
        >
          Soll der Kontakt{' '}
          <strong>
            {deletingContact ? contactDisplayName(deletingContact) : ''}
          </strong>{' '}
          wirklich gelöscht werden?
        </p>
        <p
          style={{
            fontSize: '0.8125rem',
            color: 'var(--color-error)',
            marginBottom: '1.5rem',
          }}
        >
          Alle Aktivitäten dieses Kontakts werden ebenfalls gelöscht. Deals
          bleiben erhalten, verlieren aber die Kontaktverknüpfung.
        </p>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.75rem',
          }}
        >
          <button
            onClick={() => setDeletingContact(null)}
            disabled={deleteMutation.isPending}
            style={{
              padding: '0.5rem 1rem',
              background: 'transparent',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.875rem',
              cursor: 'pointer',
              color: 'var(--color-text-secondary)',
            }}
          >
            Abbrechen
          </button>
          <button
            onClick={() =>
              deletingContact &&
              deleteMutation.mutate(deletingContact.id, {
                onSuccess: () => setDeletingContact(null),
              })
            }
            disabled={deleteMutation.isPending}
            style={{
              padding: '0.5rem 1rem',
              background: 'var(--color-error)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: deleteMutation.isPending ? 'wait' : 'pointer',
              opacity: deleteMutation.isPending ? 0.7 : 1,
            }}
          >
            {deleteMutation.isPending ? 'Löschen...' : 'Endgültig löschen'}
          </button>
        </div>
      </Modal>

      {/* Visitenkarten-Scanner */}
      <BusinessCardScanner
        isOpen={isScannerOpen}
        onClose={() => setScannerOpen(false)}
        onResult={(data) => {
          setScanPrefill(data);
          setScannerOpen(false);
          setCreateOpen(true);
        }}
      />

      {/* Kontakt aus Scan anlegen */}
      {scanPrefill && (
        <ContactFormModal
          isOpen={isCreateOpen && !!scanPrefill}
          onClose={() => { setCreateOpen(false); setScanPrefill(null); }}
          onSuccess={() => { setCreateOpen(false); setScanPrefill(null); }}
          prefill={scanPrefill}
        />
      )}
    </div>
  );
}
